import { useEffect, useRef } from "react";
import { Check, ChevronRight, Ruler, CalendarCheck } from "lucide-react";
import { MeshPhoto, meshKindFromName } from "@/components/MeshPhoto";
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const inputCls = "w-full h-12 rounded-lg border-2 border-zinc-200 px-4 focus:border-[#207024] focus:outline-none";

const formatPhone = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

function useAddressAutocomplete(ref, setAddress, setZip) {
  const acRef = useRef(null);
  useEffect(() => {
    const init = () => {
      if (!ref.current || acRef.current || !window.google?.maps?.places) return;
      acRef.current = new window.google.maps.places.Autocomplete(ref.current, {
        componentRestrictions: { country: "us" }, fields: ["address_components", "formatted_address"], types: ["address"],
      });
      acRef.current.addListener("place_changed", () => {
        const place = acRef.current.getPlace();
        if (!place?.address_components) return;
        const z = place.address_components.find((c) => c.types.includes("postal_code"));
        setAddress(place.formatted_address || ref.current.value);
        if (z) setZip(z.long_name);
      });
    };
    if (window.googleMapsReady) init();
    else {
      const prev = window.onGoogleMapsReady;
      window.onGoogleMapsReady = () => { if (prev) prev(); init(); };
    }
    return () => {
      if (acRef.current && window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(acRef.current);
      acRef.current = null;
    };
  }, [ref, setAddress, setZip]);
}

export function StepContact({ c, set }) {
  const addressRef = useRef(null);
  useAddressAutocomplete(addressRef, set.address, set.zip);
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Where should we text your quote?</h2>
        <p className="text-zinc-600 text-sm mt-1">Your price is ready. We just need your info to send it over.</p>
      </div>
      <div className="grid gap-3">
        <input className={inputCls} placeholder="First name" value={c.name} onChange={(e) => set.name(e.target.value)} autoComplete="given-name" data-testid="swing-contact-name" />
        <input className={inputCls} placeholder="(555) 123-4567" type="tel" inputMode="tel" maxLength={14} value={c.phone} onChange={(e) => set.phone(formatPhone(e.target.value))} autoComplete="tel" data-testid="swing-contact-phone" />
        <input className={inputCls} placeholder="you@example.com" type="email" value={c.email} onChange={(e) => set.email(e.target.value)} autoComplete="email" data-testid="swing-contact-email" />
        <input ref={addressRef} className={inputCls} placeholder="Street address" value={c.address} onChange={(e) => set.address(e.target.value)} autoComplete="street-address" data-testid="swing-contact-address" />
      </div>
      <p className="text-[11px] text-zinc-500 text-center">By continuing you agree to receive a text with your quote. Reply STOP to opt out.</p>
    </div>
  );
}

export function QuoteReveal({ quote, model, colorLabel, upgradeName, name, leadId }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-16" data-testid="swing-quote-reveal">
      <div className="max-w-2xl mx-auto px-4 pt-8">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-3">
            <Check className="w-4 h-4" /> Quote Ready
          </div>
          <h1 className="text-3xl font-bold text-zinc-900">Here&apos;s your quote, {name.split(" ")[0]}</h1>
          <p className="text-zinc-600 mt-2">We just texted this to you too.</p>
        </div>
        <div className="bg-white rounded-2xl border-2 border-zinc-100 shadow-sm p-5 mb-4 flex gap-4">
          <img src={model.image_url} alt={model.name} className="h-32 w-16 object-contain shrink-0" />
          <div className="text-sm text-zinc-700 space-y-1">
            <h2 className="text-lg font-bold text-zinc-900">{model.name}</h2>
            <div>Color: <span className="font-semibold">{colorLabel}</span></div>
            <div>Screen: <span className="font-semibold">{upgradeName}</span></div>
            <div>Doors: <span className="font-semibold">{quote.qty}</span></div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border-2 border-zinc-100 shadow-sm p-5 mb-4">
          <div className="space-y-1.5 text-sm">
            {quote.items.map((li, i) => (
              <div key={i}>
              <div className="flex justify-between gap-3">
                <span className="text-zinc-700">{li.name} × {li.quantity}</span>
                <span className="font-semibold text-zinc-900">{fmt(li.line_total)}</span>
              </div>
              {meshKindFromName(li.name) && <MeshPhoto kind={meshKindFromName(li.name)} compact />}
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-100 mt-3 pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-zinc-600"><span>Subtotal</span><span>{fmt(quote.subtotal)}</span></div>
            {quote.discount > 0 && <div className="flex justify-between text-[#207024] font-semibold"><span>{quote.discountName || "Discount"}</span><span>-{fmt(quote.discount)}</span></div>}
            <div className="flex justify-between text-zinc-600"><span>Sales tax</span><span>{fmt(quote.tax)}</span></div>
            <div className="flex justify-between text-xl font-bold text-[#207024] pt-2"><span>Total</span><span data-testid="swing-quote-total">{fmt(quote.total)}</span></div>
          </div>
        </div>
        <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 mb-6 text-sm text-emerald-900">
          <div className="flex items-center gap-2 font-bold mb-1"><Ruler className="w-4 h-4" /> Next step: a free measurement visit</div>
          A tech measures your doorway in person to confirm the fit, then we schedule your installation together.
        </div>
        <a
          href={leadId ? `/instant-estimate?resume=${leadId}` : "#"}
          data-testid="swing-book-measure-btn"
          className="w-full h-14 rounded-xl bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold flex items-center justify-center gap-2 shadow-lg transition-colors"
        >
          <CalendarCheck className="w-5 h-5" /> Book My Free Measurement <ChevronRight className="w-5 h-5" />
        </a>
        <p className="text-center text-xs text-zinc-500 mt-3">Prefer to talk? Just text us back.</p>
      </div>
    </div>
  );
}
